/**
 * HeroIncomingMail — chaotic "email swarm" intro using REAL physics.
 *
 * Spawns ~90 mail icons from random points along every edge of the
 * viewport with inward velocities. A requestAnimationFrame loop
 * advances position, rotation, and resolves elastic collisions
 * (icon-vs-icon AND icon-vs-edge) so the swarm genuinely bounces
 * off itself and the walls — no pre-baked CSS path, no scripted
 * convergence. After ~6.5s the whole layer fades out and unmounts.
 *
 * Performance notes:
 *   - DOM transforms updated directly via refs (no React state in
 *     the hot loop).
 *   - O(n²) collision pass is ~8,100 checks/frame at n=90 — well
 *     within budget at 60 fps for the short intro window.
 *   - `prefers-reduced-motion` skips the swarm entirely.
 */
import { useEffect, useRef, useState } from "react";

const BRAND_COLORS = [
  "#9200C7", "#0083D3", "#62BB46", "#FF6B00",
  "#C026D3", "#7C3AED", "#EC4899", "#0EA5E9",
  "#14B8A6", "#F59E0B",
];

const r = (a, b) => a + Math.random() * (b - a);
const sign = () => (Math.random() < 0.5 ? -1 : 1);

// Mail-icon SVG inlined so we don't pay React-render cost per piece.
// Stroke is `currentColor` so each piece colour is set via CSS color.
const MAIL_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" ' +
  'stroke-linecap="round" stroke-linejoin="round" width="100%" height="100%">' +
  '<rect width="20" height="16" x="2" y="4" rx="2"/>' +
  '<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>';

function spawnPiece(W, H) {
  // Spawn on a random screen edge, inject inward velocity.
  const edge = Math.floor(Math.random() * 4); // 0=top, 1=right, 2=bottom, 3=left
  let x, y, vx, vy;
  const speed = r(380, 720); // px/sec — 2× faster intro per user request
  if (edge === 0) {
    x = r(0, W);
    y = -40;
    vx = r(-1, 1) * speed * 0.5;
    vy = r(0.5, 1) * speed;
  } else if (edge === 1) {
    x = W + 40;
    y = r(0, H);
    vx = -r(0.5, 1) * speed;
    vy = r(-1, 1) * speed * 0.5;
  } else if (edge === 2) {
    x = r(0, W);
    y = H + 40;
    vx = r(-1, 1) * speed * 0.5;
    vy = -r(0.5, 1) * speed;
  } else {
    x = -40;
    y = r(0, H);
    vx = r(0.5, 1) * speed;
    vy = r(-1, 1) * speed * 0.5;
  }
  const size = Math.round(r(28, 56));
  return {
    x, y, vx, vy,
    rot: r(0, 360),
    rotV: r(-360, 360) * sign(), // deg/sec
    size,
    radius: size * 0.45, // collision radius — slightly smaller than half-size
    color: BRAND_COLORS[Math.floor(Math.random() * BRAND_COLORS.length)],
    alpha: 0,
    born: performance.now() / 1000,
    el: null,
  };
}

export default function HeroIncomingMail({ count = 90, totalMs = 6500 }) {
  const [visible, setVisible] = useState(true);
  const containerRef = useRef(null);
  const piecesRef = useRef([]);
  const rafRef = useRef(0);
  const startRef = useRef(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      if (reduce) { setVisible(false); return; }
    }
    const t = setTimeout(() => setVisible(false), totalMs);
    return () => clearTimeout(t);
  }, [totalMs]);

  // Physics loop — mount once, run until layer unmounts.
  useEffect(() => {
    if (!visible) return;
    const container = containerRef.current;
    if (!container) return;

    const W = container.clientWidth;
    const H = container.clientHeight;

    // Mobile: drop density to ~40% so the intro stays smooth on
    // low-end phones (still plenty of icons for the wow effect).
    const isMobile = typeof window !== "undefined"
      && window.matchMedia?.("(max-width: 767px)").matches;
    const effectiveCount = isMobile ? Math.max(20, Math.floor(count * 0.4)) : count;

    // Build pieces + their DOM nodes once.
    const pieces = [];
    for (let i = 0; i < effectiveCount; i++) {
      const p = spawnPiece(W, H);
      const el = document.createElement("span");
      el.className = "mail-swarm-piece";
      el.style.width = `${p.size}px`;
      el.style.height = `${p.size}px`;
      el.style.color = p.color;
      el.style.opacity = "0";
      el.innerHTML = MAIL_SVG;
      container.appendChild(el);
      p.el = el;
      // Stagger spawns so they don't all appear in frame 1.
      p.spawnAt = i * (0.8 / effectiveCount); // launch window 0.8s — half of original for 2× pace
      p.alive = false;
      pieces.push(p);
    }
    piecesRef.current = pieces;

    startRef.current = performance.now() / 1000;
    const totalSec = totalMs / 1000;
    const fadeStart = totalSec - 1.6;
    let prev = performance.now() / 1000;

    const step = () => {
      const now = performance.now() / 1000;
      const dt = Math.min(0.05, now - prev);
      prev = now;
      const elapsed = now - startRef.current;

      // Activate pieces whose spawn time has arrived.
      for (let i = 0; i < pieces.length; i++) {
        const p = pieces[i];
        if (!p.alive && elapsed >= p.spawnAt) p.alive = true;
      }

      // Integrate
      for (let i = 0; i < pieces.length; i++) {
        const p = pieces[i];
        if (!p.alive) continue;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.rotV * dt;
        // Wall bounce
        if (p.x < p.radius && p.vx < 0) { p.x = p.radius; p.vx = -p.vx * 0.92; }
        if (p.x > W - p.radius && p.vx > 0) { p.x = W - p.radius; p.vx = -p.vx * 0.92; }
        if (p.y < p.radius && p.vy < 0) { p.y = p.radius; p.vy = -p.vy * 0.92; }
        if (p.y > H - p.radius && p.vy > 0) { p.y = H - p.radius; p.vy = -p.vy * 0.92; }
      }

      // Elastic collisions — O(n²), fine for n≤120 over 6.5s.
      for (let i = 0; i < pieces.length; i++) {
        const a = pieces[i];
        if (!a.alive) continue;
        for (let j = i + 1; j < pieces.length; j++) {
          const b = pieces[j];
          if (!b.alive) continue;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const d2 = dx * dx + dy * dy;
          const rsum = a.radius + b.radius;
          if (d2 > 0 && d2 < rsum * rsum) {
            const d = Math.sqrt(d2);
            const nx = dx / d;
            const ny = dy / d;
            // Separate to avoid sticking
            const overlap = (rsum - d) / 2;
            a.x -= nx * overlap; a.y -= ny * overlap;
            b.x += nx * overlap; b.y += ny * overlap;
            // Equal-mass elastic collision along normal
            const va = a.vx * nx + a.vy * ny;
            const vb = b.vx * nx + b.vy * ny;
            const dV = vb - va;
            a.vx += dV * nx; a.vy += dV * ny;
            b.vx -= dV * nx; b.vy -= dV * ny;
            // Add a touch of angular kick — feels like spinning impact
            a.rotV += (Math.random() - 0.5) * 240;
            b.rotV += (Math.random() - 0.5) * 240;
          }
        }
      }

      // Render — write transforms / opacities to DOM
      for (let i = 0; i < pieces.length; i++) {
        const p = pieces[i];
        if (!p.alive) { p.el.style.opacity = "0"; continue; }
        // Fade in for the first 0.25s after spawn; fade out near the end.
        const age = elapsed - p.spawnAt;
        let alpha = 1;
        if (age < 0.25) alpha = age / 0.25;
        if (elapsed > fadeStart) {
          const k = (elapsed - fadeStart) / (totalSec - fadeStart);
          alpha *= Math.max(0, 1 - k);
        }
        p.el.style.opacity = String(alpha);
        p.el.style.transform =
          `translate3d(${p.x - p.size / 2}px, ${p.y - p.size / 2}px, 0) rotate(${p.rot}deg)`;
      }

      if (elapsed < totalSec) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafRef.current);
      for (const p of pieces) { try { p.el.remove(); } catch (_e) {/* noop */} }
      piecesRef.current = [];
    };
  }, [visible, count, totalMs]);

  if (!visible) return null;
  return (
    <div
      ref={containerRef}
      className="mail-swarm-layer"
      aria-hidden="true"
      data-testid="hero-incoming-mail-layer"
    />
  );
}
